import React, { useState, useEffect, useMemo } from 'react';
import { FormationCell, Formation, Team, TemplateFormation } from '../types';
import MobileTacticsBoard from '../components/MobileTacticsBoard';
import { DefaultFormations } from '../data/DefaultFormations';
import { useDialog } from '../components/DialogProvider';
import { BottomSheetSelect, SelectOption } from '../components/BottomSheetSelect';
import '../App.css';

interface TacticsTabProps {
  team: Team;
  onChange: (updatedTeam: Team) => void;
}

// Helper to match player positions to template requirements
function getArchetype(position: string): string {
  const norm = (position || '').toLowerCase();
  if (norm.includes('lineman')) return 'Lineman';
  if (norm.includes('blitzer')) return 'Blitzer';
  if (norm.includes('thrower')) return 'Thrower';
  if (norm.includes('catcher')) return 'Catcher';
  if (norm.includes('runner')) return 'Runner';
  if (norm.includes('blocker')) return 'Blocker';
  if (
    norm.includes('big guy') || norm.includes('troll') || norm.includes('ogre') || 
    norm.includes('minotaur') || norm.includes('treeman') || norm.includes('kroxigor') || 
    norm.includes('yhetee') || norm.includes('mummy')
  ) return 'Big Guy';
  return 'Special';
}

export default function TacticsTab({ team, onChange }: TacticsTabProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeType, setActiveType] = useState<'Offensive' | 'Defensive'>('Offensive');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [draftName, setDraftName] = useState('');
  const { confirm, toast } = useDialog();

  const layout = useMemo(() => team.activeFormationLayout || [], [team.activeFormationLayout]);
  
  const currentForm = useMemo(() => {
    return (team.formations || []).find((f) => f.id === team.activeFormationId) || null;
  }, [team.formations, team.activeFormationId]);

  // Sync draft name field when a saved formation is loaded
  useEffect(() => {
    setDraftName(currentForm?.name || '');
  }, [currentForm?.name]);

  const setLayout = (newLayout: FormationCell[]) => {
    onChange({
      ...team,
      activeFormationLayout: newLayout,
    });
  };

  // Responsive switch on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formations = team.formations || [];

  // Check if layout is dirty compared to currentForm
  const isDirty = useMemo(() => {
    if (!currentForm) return false; // unallocated state, no warning needed
    if (layout.length !== currentForm.layout.length) return true;
    for (const l of layout) {
      if (!currentForm.layout.find(cl => Number(cl.playerNumber) === Number(l.playerNumber) && cl.row === l.row && cl.col === l.col)) {
        return true;
      }
    }
    return false;
  }, [layout, currentForm]);

  const confirmDiscard = async () => {
    if (isDirty) {
      return await confirm("You have unsaved changes to your formation. Do you want to discard them?", "Discard Changes?");
    }
    return true;
  };

  // Validation Warnings
  const warnings = useMemo(() => {
    const w: string[] = [];
    if (layout.length === 0) return w;

    if (layout.length > 11) w.push("Too many players on the pitch (Max 11).");
    
    const losCount = layout.filter(c => c.row === 14).length;
    if (losCount < 3) w.push("Need at least 3 players on the Line of Scrimmage (Row 14).");

    const leftWide = layout.filter(c => c.col >= 1 && c.col <= 4).length;
    if (leftWide > 2) w.push("Too many players in Left Wide Zone (Max 2).");

    const rightWide = layout.filter(c => c.col >= 12 && c.col <= 15).length;
    if (rightWide > 2) w.push("Too many players in Right Wide Zone (Max 2).");

    return w;
  }, [layout]);

  const handleApplyTemplate = (template: TemplateFormation) => {
    // Autofill logic
    let availablePlayers = [...team.players].filter(p => p.name || p.position);
    const newLayout: FormationCell[] = [];

    // First pass: strict match on archetype
    for (const cell of template.layout) {
      const matchIdx = availablePlayers.findIndex(p => getArchetype(p.position) === cell.positionType);
      if (matchIdx >= 0) {
        newLayout.push({ playerNumber: availablePlayers[matchIdx].number, row: cell.row, col: cell.col });
        availablePlayers.splice(matchIdx, 1);
      } else {
        // Second pass: fallback to any available player if strict match fails
        if (availablePlayers.length > 0) {
          newLayout.push({ playerNumber: availablePlayers[0].number, row: cell.row, col: cell.col });
          availablePlayers.splice(0, 1);
        }
      }
    }

    onChange({
      ...team,
      activeFormationLayout: newLayout,
      activeFormationId: null, // Move to unallocated state so they can save it
    });
    setActiveType(template.type);
  };

  const handleSave = (name: string) => {
    const now = new Date().toISOString();
    let updated: Formation[];
    let targetId = team.activeFormationId;

    if (targetId) {
      updated = formations.map((f) =>
        f.id === targetId ? { ...f, name, layout, updatedAt: now } : f
      );
    } else {
      const newId = crypto.randomUUID();
      const f: Formation = {
        id: newId,
        name,
        type: activeType,
        layout,
        createdAt: now,
        updatedAt: now,
      };
      updated = [...formations, f];
      targetId = newId;
    }
    
    onChange({
      ...team,
      formations: updated,
      activeFormationId: targetId,
      activeFormationLayout: layout,
    });
    toast(`Saved formation "${name}"`, "success");
  };

  const MobileBoardWithWarnings = (isDesktop: boolean) => (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {warnings.length > 0 && (
        <div style={{
          backgroundColor: 'rgba(220, 38, 38, 0.9)',
          color: 'white',
          padding: '8px 12px',
          fontSize: '13px',
          fontWeight: 'bold',
          borderRadius: '4px',
          margin: '0 0 12px 0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          zIndex: 10
        }}>
          ⚠️ Warnings:
          <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
      <MobileTacticsBoard
        team={team}
        layout={layout}
        onChange={setLayout}
        selectedId={selectedId}
        onSelect={setSelectedId}
        isDesktop={isDesktop}
      />
    </div>
  );

  // Mobile Layout
  if (isMobile) {
    return MobileBoardWithWarnings(false);
  }

  // Generate options for the bottom sheet selects
  const customFormationOptions: SelectOption[] = [
    { value: '', label: '-- Select Custom --' },
    ...formations
      .filter((f) => f.type === activeType)
      .map((f) => ({ value: f.id, label: f.name || '—untitled—' })),
  ];

  const templateOptions: SelectOption[] = [
    { value: '', label: 'Apply Standard template...', disabled: true },
    ...DefaultFormations
      .filter((t) => t.type === activeType)
      .map((t) => ({ value: t.name, label: t.name })),
  ];

  // Desktop Layout
  return (
    <div className="unified-desktop-tactics" style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 16px 24px 16px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }}>
      {/* Sticky Formations Status Bar directly under Roster/Tactics tab buttons */}
      <div style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 20, 
        backgroundColor: '#1e293b', 
        border: '1px solid #334155', 
        borderRadius: '8px', 
        padding: '12px 16px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        gap: '16px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
      }}>
        {/* 1. Offensive / Defensive Toggles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#94a3b8' }}>Type:</span>
          <div style={{ display: 'flex', background: '#0f172a', borderRadius: '6px', padding: '2px' }}>
            {['Offensive', 'Defensive'].map(t => (
              <button
                key={t}
                onClick={() => {
                  setActiveType(t as any);
                }}
                style={{
                  background: t === activeType ? '#3b82f6' : 'transparent',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* 2. Custom Formations Dropdown & Delete */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '180px' }}>
          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#94a3b8' }}>Saved:</span>
          <BottomSheetSelect
            value={team.activeFormationId || ''}
            options={customFormationOptions}
            placeholder="Select a custom formation"
            title="Custom Formations"
            onChange={async (fid) => {
              if (!fid) {
                if (!await confirmDiscard()) return;
                onChange({ ...team, activeFormationId: null, activeFormationLayout: [] });
                return;
              }
              if (!await confirmDiscard()) return;
              const f = formations.find((x) => x.id === fid)!;
              setActiveType(f.type);
              onChange({
                ...team,
                activeFormationId: f.id,
                activeFormationLayout: f.layout,
              });
            }}
          />
          {team.activeFormationId && (
            <button
              onClick={async () => {
                if (await confirm("Delete this formation?", "Delete Formation")) {
                  const fid = team.activeFormationId!;
                  const updated = formations.filter((f) => f.id !== fid);
                  onChange({
                    ...team,
                    formations: updated,
                    activeFormationId: null,
                    activeFormationLayout: [],
                  });
                  toast("Formation deleted successfully!", "success");
                }
              }}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 12px',
                cursor: 'pointer',
                fontSize: '13px',
                flexShrink: 0
              }}
            >
              🗑
            </button>
          )}
        </div>

        {/* 3. Name Input & Save Button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="text"
            placeholder="New name..."
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
            style={{
              padding: '8px 10px',
              background: '#0f172a',
              color: 'white',
              border: '1px solid #475569',
              borderRadius: '6px',
              fontSize: '13px',
              width: '140px'
            }}
          />
          <button
            onClick={() => { if (draftName.trim()) handleSave(draftName.trim()); }}
            disabled={!draftName.trim()}
            style={{
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 12px',
              fontWeight: 'bold',
              fontSize: '12px',
              cursor: draftName.trim() ? 'pointer' : 'default',
              opacity: draftName.trim() ? 1 : 0.5
            }}
          >
            Save
          </button>
        </div>

        {/* 4. Default Templates Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#94a3b8' }}>Template:</span>
          <BottomSheetSelect
            value=""
            options={templateOptions}
            placeholder="Apply Standard template..."
            title="Standard Formations"
            onChange={async (val) => {
              if (val) {
                const tmpl = DefaultFormations.find(t => t.name === val);
                if (tmpl) {
                  if (!await confirmDiscard()) return;
                  handleApplyTemplate(tmpl);
                  toast(`Applied formation template "${tmpl.name}"`, "success");
                }
              }
            }}
            style={{ width: '220px' }}
          />
        </div>
      </div>

      {/* Centered Coaching Board container */}
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        {MobileBoardWithWarnings(true)}
      </div>
    </div>
  );
}
